import { useEffect, useState } from 'react';
import { endpoints, type MealSlot, type RecipeSummary } from '../api/endpoints';
import { RecipeCard } from './RecipeCard';
import { Skeleton } from './states';

// Слот → курс выбора по умолчанию. lunch_main/dinner_main грузят только 'main'
// (side/baking доступны через «показать все», убирающую фильтр курса). Спека §4.
const SLOT_COURSE: Record<MealSlot, string> = {
  breakfast: 'breakfast',
  lunch_soup: 'soup',
  lunch_main: 'main',
  dinner_main: 'main',
  lunch_salad: 'salad',
  dinner_salad: 'salad',
};
const SLOT_TITLE: Record<MealSlot, string> = {
  breakfast: 'Завтрак',
  lunch_soup: 'Первое',
  lunch_main: 'Второе (обед)',
  dinner_main: 'Второе (ужин)',
  lunch_salad: 'Салат (обед)',
  dinner_salad: 'Салат (ужин)',
};

export function SlotPickerModal({
  slot,
  onPick,
  onClose,
}: {
  slot: MealSlot;
  onPick: (recipeId: string) => void;
  onClose: () => void;
}) {
  const [items, setItems] = useState<RecipeSummary[] | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    // Перезагрузка списка при смене слота/режима — намеренный fetch-in-effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setItems(null);
    // course='' → бэкенд игнорит фильтр (endpoints.getRecipesByCourse шлёт ?limit=100 без course).
    const load = showAll ? endpoints.getRecipesByCourse('') : endpoints.getRecipesByCourse(SLOT_COURSE[slot]);
    void load.then((r) => setItems(r.items)).catch(() => setItems([]));
  }, [slot, showAll]);

  // Escape закрывает (как CookedModal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="slot-picker-title" className="fixed inset-0 z-30 flex flex-col bg-cream">
      <div className="flex items-center justify-between border-b border-black/5 px-4 py-3">
        <h2 id="slot-picker-title" className="text-lg font-bold text-ink">{SLOT_TITLE[slot]}</h2>
        <button onClick={onClose} aria-label="Закрыть" className="text-ink/60">
          ✕
        </button>
      </div>
      <div className="flex items-center gap-3 px-4 py-2">
        <button onClick={() => setShowAll((v) => !v)} className="text-sm text-primary-700 underline">
          {showAll ? 'Показать по типу' : 'Показать все'}
        </button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-8">
        {items === null ? (
          <Skeleton className="h-36 w-full" />
        ) : items.length === 0 ? (
          <p className="text-ink/60">Нет подходящих рецептов.</p>
        ) : (
          items.map((r) => (
            <RecipeCard
              key={r.id}
              title={r.title ?? ''}
              imageUrl={r.imageUrl}
              cookTimeMin={r.cookTimeMin}
              servings={r.servings}
              onClick={() => onPick(r.id!)}
            />
          ))
        )}
      </div>
    </div>
  );
}
